import { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@/ds";
import { useObraEmbarcacoes } from "@/modules/obras";
import {
  errosDaLinha,
  linhaTemErros,
  periodoDaObraNoWizard,
  readEquipe,
} from "../hooks/nova-obra.equipe";
import { useAlocacoesPeriodo } from "../hooks/use-alocacoes-periodo";
import { useNovaObra } from "../hooks/nova-obra.context";
import type { EquipeLinha } from "../hooks/nova-obra.equipe";
import { todayIso } from "@/modules/carga-trabalho/carga-trabalho.utils";
import { EQUIPE_OBRA_FORBIDDEN_MESSAGE } from "@/modules/recursos/use-definir-equipe-obra";
import { sugerirPeriodo } from "@/modules/recursos/equipe-obra.rules";
import { useEquipeObraOpcoes } from "@/modules/recursos/use-equipe-obra-opcoes";
import type { PapelObra } from "@/modules/recursos/papeis-obra.types";
import { NOVA_OBRA_TITLE } from "../nova-obra.constants";
import {
  EQUIPE_ADDED_TOAST,
  EQUIPE_REMOVED_TOAST,
  EQUIPE_SELECT_PROFISSIONAL_MESSAGE,
  EQUIPE_TAB_CALENDARIO,
  EQUIPE_TAB_PROFISSIONAIS,
  EQUIPE_UPDATED_TOAST,
} from "@/shared/components/equipe-obra/equipe-obra.constants";
import { novasDaEquipe, obraDaNovaObra } from "../hooks/equipe-verificacoes";
import { useVerificacoesEquipe } from "../hooks/use-verificacoes-equipe";
import type { TabId, EtapaEquipeProps } from "./types-etapa-equipe";
import { detectarConflitos, resumirPapeisObrigatorios } from "@/modules/recursos";

const ABAS: { id: TabId; label: string }[] = [
  { id: "profissionais", label: EQUIPE_TAB_PROFISSIONAIS },
  { id: "calendario", label: EQUIPE_TAB_CALENDARIO },
];

/** A obra ainda não existe: nenhuma alocação da carga é "da própria obra". */
const OBRA_SEM_ID = { id: "", codigo: "", nome: "" };

export function useEtapaEquipe({ guardaAvancar, onVerificandoChange }: EtapaEquipeProps) {
  const { state, salvarEtapa, avancar } = useNovaObra();
  const toast = useToast();
  const counter = useRef(0);

  const dados = useMemo(() => readEquipe(state.etapas.equipe), [state.etapas.equipe]);
  const linhas = dados.linhas;
  const periodo = useMemo(
    () => periodoDaObraNoWizard(state.etapas.duracao),
    [state.etapas.duracao],
  );
  const today = useMemo(() => todayIso(), []);
  // Sem período ainda não há o que consultar: usa hoje só para a consulta não ir vazia.
  const opcoes = useEquipeObraOpcoes(periodo?.inicio ?? today, periodo?.fim ?? today);

  // O código da obra só existe depois da criação: a intro do modal cita a embarcação (Figma 12227:420586).
  const embarcacoes = useObraEmbarcacoes();
  const embarcacao = embarcacoes.data.find((item) => item.id === state.obra.embarcacaoId);

  const [tab, setTab] = useState<TabId>("profissionais");
  const [requiredOpen, setRequiredOpen] = useState(false);
  const selecionarAba = (id: TabId) => setTab(id);
  const alternarPapeisObrigatorios = () => setRequiredOpen((open) => !open);
  const [formError, setFormError] = useState<string | null>(null);

  // As ações leem as linhas mais recentes pela ref, que acompanha o estado depois de cada commit.
  const linhasRef = useRef(linhas);
  useEffect(() => {
    linhasRef.current = linhas;
  }, [linhas]);

  const salvar = (proximas: EquipeLinha[]) => salvarEtapa("equipe", { linhas: proximas });

  const notified = useRef({ papeis: false, profissionais: false, forbidden: false });
  useEffect(() => {
    if (opcoes.status === "loading") return;
    const notify = (summary: string) => toast.error(summary);

    if (opcoes.forbidden && !notified.current.forbidden) {
      notified.current.forbidden = true;
      notify(EQUIPE_OBRA_FORBIDDEN_MESSAGE);
      return;
    }
    if (opcoes.profissionaisError && !notified.current.profissionais && !opcoes.forbidden) {
      notified.current.profissionais = true;
      notify(opcoes.profissionaisError);
    }
    if (opcoes.papeisError && !notified.current.papeis && !opcoes.forbidden) {
      notified.current.papeis = true;
      notify(opcoes.papeisError);
    }
  }, [opcoes, toast]);

  const papeisById = useMemo(
    () => new Map(opcoes.papeis.map((papel) => [papel.id, papel])),
    [opcoes.papeis],
  );
  const obrigatorios = useMemo(
    () => opcoes.papeis.filter((papel) => papel.obrigatorio),
    [opcoes.papeis],
  );
  const papeisOpcoes = useMemo(
    () => opcoes.papeis.map((papel) => ({ value: papel.id, label: papel.nome })),
    [opcoes.papeis],
  );
  const adicionados = useMemo(() => new Set(linhas.map((linha) => linha.papelObraId)), [linhas]);

  const periodoRef = periodo ?? { inicio: today, fim: today };

  function patchRow(
    id: string,
    patch: Partial<EquipeLinha>,
    clear: (keyof EquipeLinha["errors"])[] = [],
  ) {
    setFormError(null);
    salvar(
      linhasRef.current.map((linha) => {
        if (linha.id !== id) return linha;
        const errors = { ...linha.errors };
        for (const key of clear) delete errors[key];
        return { ...linha, ...patch, errors };
      }),
    );
  }

  /** Trocar o papel sugere o período da janela dele (Impasse 21); as demais mudanças vão como vieram. */
  function changeRow(
    id: string,
    patch: Partial<EquipeLinha>,
    clear: (keyof EquipeLinha["errors"])[],
  ) {
    const atual = linhasRef.current.find((linha) => linha.id === id);
    if (patch.papelObraId && atual && patch.papelObraId !== atual.papelObraId) {
      const papel = papeisById.get(patch.papelObraId);
      const sugestao = papel && periodo ? sugerirPeriodo(papel.janela, periodo) : null;
      patchRow(id, sugestao ? { ...patch, ...sugestao } : patch, clear);
      return;
    }
    patchRow(id, patch, clear);
  }

  function addRow(papel: PapelObra) {
    if (!periodo) return;
    counter.current += 1;
    const sugestao = sugerirPeriodo(papel.janela, periodo);

    setFormError(null);
    salvar([
      ...linhasRef.current,
      {
        id: `alocacao-${Date.now()}-${counter.current}`,
        papelObraId: papel.id,
        usuarioId: null,
        inicio: sugestao.inicio,
        fim: sugestao.fim,
        editing: true,
        isNew: true,
        backup: null,
        errors: {},
      },
    ]);
  }

  function confirmRow(id: string) {
    const linha = linhasRef.current.find((candidata) => candidata.id === id);
    if (!linha) return;

    const errors = errosDaLinha(linha, linhasRef.current, periodoRef);
    if (linhaTemErros(errors)) {
      salvar(linhasRef.current.map((l) => (l.id === id ? { ...l, errors } : l)));
      return;
    }

    salvar(
      linhasRef.current.map((l) =>
        l.id === id ? { ...l, editing: false, isNew: false, backup: null, errors: {} } : l,
      ),
    );
    toast.success(linha.isNew ? EQUIPE_ADDED_TOAST : EQUIPE_UPDATED_TOAST);
  }

  function cancelRow(id: string) {
    salvar(
      linhasRef.current.flatMap((linha) => {
        if (linha.id !== id) return [linha];
        if (linha.isNew || !linha.backup) return [];
        return [{ ...linha, ...linha.backup, editing: false, backup: null, errors: {} }];
      }),
    );
  }

  function startEdit(id: string) {
    salvar(
      linhasRef.current.map((linha) =>
        linha.id === id
          ? {
              ...linha,
              editing: true,
              backup: { usuarioId: linha.usuarioId, inicio: linha.inicio, fim: linha.fim },
            }
          : linha,
      ),
    );
  }

  function removeRow(id: string) {
    const linha = linhasRef.current.find((candidata) => candidata.id === id);
    salvar(linhasRef.current.filter((candidata) => candidata.id !== id));
    setFormError(null);
    if (linha?.usuarioId) toast.success(EQUIPE_REMOVED_TOAST);
  }

  /** Valida todas as alocações em tela (inclui o período do wizard); devolve se estão válidas. */
  function validateAll(): boolean {
    let missing = false;
    const next = linhasRef.current.map((linha) => {
      const errors = errosDaLinha(linha, linhasRef.current, periodoRef);
      if (!linha.usuarioId) missing = true;
      return { ...linha, errors };
    });

    salvar(next);
    // O fim das verificações pode gravar a justificativa antes do próximo commit.
    linhasRef.current = next;
    setFormError(missing ? EQUIPE_SELECT_PROFISSIONAL_MESSAGE : null);
    if (next.some((linha) => linha.editing)) return false;

    return next.every((linha) => !linhaTemErros(linha.errors));
  }

  const obraVerificada = obraDaNovaObra(embarcacao?.nome ?? NOVA_OBRA_TITLE, periodoRef);
  const novas = useMemo(() => novasDaEquipe(linhas, papeisById), [linhas, papeisById]);

  // Conflitos com outras obras no período do wizard (Figma 9141:352541 e 9129:353328): de todos os candidatos, para o menu
  // também avisar (sem ids a consulta traz quem tem alocação no período; sem período não consulta). Falha na consulta só
  // omite as tags; a verificação do "Próxima etapa" consulta de novo.
  const carga = useAlocacoesPeriodo({
    periodo: periodoRef,
    profissionalIds: periodo ? undefined : [],
  });
  const conflitosPorLinha = useMemo(() => {
    const porLinha = new Map<string, Map<string, number>>();
    if (carga.status !== "success") return porLinha;
    for (const linha of linhas) {
      const contagem = new Map<string, number>();
      for (const profissional of carga.profissionais) {
        const total = detectarConflitos(
          [
            {
              id: linha.id,
              usuarioId: profissional.id,
              papelObraId: linha.papelObraId,
              inicio: linha.inicio,
              fim: linha.fim,
            },
          ],
          OBRA_SEM_ID,
          [profissional],
        ).length;
        if (total > 0) contagem.set(profissional.id, total);
      }
      porLinha.set(linha.id, contagem);
    }
    return porLinha;
  }, [carga.status, carga.profissionais, linhas]);
  const conflitosDe = (linha: EquipeLinha, usuarioId: string) =>
    conflitosPorLinha.get(linha.id)?.get(usuarioId) ?? 0;

  // Mesma sequência do Salvar de Recursos (HU 2059, nota da PO): conflito com outra obra →
  // indisponibilidade → equipe incompleta. Justificar não bloqueia: a etapa avança e a
  // justificativa vai no POST da criação.
  const verificacoes = useVerificacoesEquipe({
    obra: obraVerificada,
    validar: validateAll,
    novas,
    resumoObrigatorios: resumirPapeisObrigatorios(
      obrigatorios,
      linhas,
      opcoes.profissionais,
      periodoRef,
    ),
    listagem: opcoes.profissionais,
    onErroVerificacao: (summary) => toast.error(summary),
    acaoFinal: (justificativa) => {
      salvarEtapa("equipe", { linhas: linhasRef.current, justificativa: justificativa ?? null });
      avancar();
    },
  });

  // O rodapé do wizard consulta a guarda a cada clique: sempre a versão do último render.
  useEffect(() => {
    guardaAvancar.current = () => {
      if (linhasRef.current.length === 0 && obrigatorios.length === 0) return false;
      if (!periodo) return true;
      void verificacoes.verificar();
      return true;
    };
    const guarda = guardaAvancar;
    return () => {
      guarda.current = null;
    };
  });

  useEffect(() => {
    onVerificandoChange?.(verificacoes.verificando);
  }, [onVerificandoChange, verificacoes.verificando]);
  useEffect(() => () => onVerificandoChange?.(false), [onVerificandoChange]);

  const abas = ABAS.map((aba) => ({
    ...aba,
    ativa: aba.id === tab,
    domId: `equipe-obra-tab-${aba.id}`,
  }));
  const etapaVerificacao = verificacoes.etapa;

  return {
    linhas,
    periodo,
    today,
    opcoes,
    abas,
    abaAtivaId: `equipe-obra-tab-${tab}`,
    calendarioAtivo: tab === "calendario",
    selecionarAba,
    requiredOpen,
    alternarPapeisObrigatorios,
    carregandoOpcoes: opcoes.status === "loading",
    adicionarPapelDesabilitado: opcoes.status === "loading" || opcoes.forbidden || !periodo,
    conflitos: etapaVerificacao?.tipo === "conflitos" ? etapaVerificacao : null,
    indisponiveis: etapaVerificacao?.tipo === "indisponivel" ? etapaVerificacao : null,
    itensIncompletos: etapaVerificacao?.tipo === "incompleta" ? etapaVerificacao.itens : null,
    formError,
    obrigatorios,
    papeisOpcoes,
    adicionados,
    changeRow,
    addRow,
    confirmRow,
    cancelRow,
    startEdit,
    removeRow,
    obraVerificada,
    novas,
    calendarioVazio: novas.length === 0,
    conflitosDe,
    verificacoes,
  };
}
